import {
  findEnvironment,
  findVehicle,
  LIMITS,
  nameSchema,
  type Course,
  type Project,
} from '@rcs/data-schema';
import type { Command } from './command';
import type { EditorState } from './state';

/**
 * プロジェクトのコースの操作（FR-CRS-001, 002）。どれも純粋関数で、元のプロジェクトは変えない。
 * 新しいコースの ID・日時は呼び出し側が渡す（乱数・実時刻を使わない）。
 * 操作できないとき（上限・下限・存在しない ID・不正な名前）は `undefined`。
 */

/** コースを追加できるか（上限 200 本）。 */
export function canAddCourse(project: Project): boolean {
  return project.courses.length < LIMITS.maxCourses;
}

/** コースを削除できるか（最後の 1 本は削除できない）。 */
export function canRemoveCourse(project: Project, id: string): boolean {
  return project.courses.length > 1 && project.courses.some((course) => course.id === id);
}

/**
 * コースを追加する。`afterId` のコースの次（なければ末尾）に入れる。
 * ID が重複する・上限を超えるときは `undefined`。
 */
export function addCourse(project: Project, course: Course, afterId?: string): Project | undefined {
  if (!canAddCourse(project) || project.courses.some((c) => c.id === course.id)) return undefined;
  const index = project.courses.findIndex((c) => c.id === afterId);
  const courses = [...project.courses];
  courses.splice(index < 0 ? courses.length : index + 1, 0, course);
  return { ...project, courses };
}

/** 同じ ID のコースを置き換える。なければ `undefined`。 */
export function replaceCourse(project: Project, course: Course): Project | undefined {
  if (!project.courses.some((c) => c.id === course.id)) return undefined;
  return { ...project, courses: project.courses.map((c) => (c.id === course.id ? course : c)) };
}

/** コースを削除する。最後の 1 本・存在しない ID は `undefined`。 */
export function removeCourse(project: Project, id: string): Project | undefined {
  if (!canRemoveCourse(project, id)) return undefined;
  return { ...project, courses: project.courses.filter((c) => c.id !== id) };
}

/**
 * コースを削除したあとに選ぶコース：削除したコースの次（末尾なら前）。
 * 削除できないときは `undefined`。
 */
export function neighborAfterRemoval(project: Project, id: string): Course | undefined {
  if (!canRemoveCourse(project, id)) return undefined;
  const index = project.courses.findIndex((c) => c.id === id);
  return project.courses[index + 1] ?? project.courses[index - 1];
}

/**
 * コースを複製する。制御点・区間・車両・環境の指定をそのまま写し、ID・名前・日時を新しくする。
 * 名前は「〜のコピー」（100 文字に収める）。
 *
 * @param newId 新しいコースの ID（UUID）
 * @param now 作成日時（ISO 8601）
 */
export function duplicateCourse(course: Course, newId: string, now: string): Course {
  const suffix = 'のコピー';
  const name = `${course.name.slice(0, 100 - suffix.length)}${suffix}`;
  return { ...course, id: newId, name, createdAt: now, updatedAt: now };
}

/** 既存のコースと重ならない、新しいコースの名前（「コース 2」など）。 */
export function nextCourseName(project: Project, base = 'コース'): string {
  const names = new Set(project.courses.map((c) => c.name));
  for (let n = project.courses.length + 1; ; n += 1) {
    const name = `${base} ${String(n)}`;
    if (!names.has(name)) return name;
  }
}

/**
 * 編集中のコースの名前を変える操作（Undo / Redo の単位）。前後の空白は除く。
 * 名前が不正（空・100 文字超）・変わらないときは `undefined`。
 */
export function renameCourseCommand(
  state: EditorState,
  name: string,
): Command<EditorState> | undefined {
  const parsed = nameSchema.safeParse(name);
  if (!parsed.success) return undefined;
  const next = parsed.data;
  const previous = state.course.name;
  if (next === previous) return undefined;
  return {
    label: 'コース名を変更',
    apply: (s) => ({ ...s, course: { ...s.course, name: next } }),
    revert: (s) => ({ ...s, course: { ...s.course, name: previous } }),
  };
}

/**
 * 編集中のコースの既定の車両を変える操作（Undo / Redo の単位）。
 * 組み込みの車両（`findVehicle` で見つかるもの）だけを選べる。見つからない ID・変化なしは `undefined`。
 */
export function setVehicleCommand(
  state: EditorState,
  vehicleId: string,
): Command<EditorState> | undefined {
  if (findVehicle(vehicleId) === undefined) return undefined;
  const previous = state.course.defaultVehicleId;
  if (previous === vehicleId) return undefined;
  return {
    label: '車両を変更',
    apply: (s) => ({ ...s, course: { ...s.course, defaultVehicleId: vehicleId } }),
    revert: (s) => {
      const { defaultVehicleId: _removed, ...rest } = s.course;
      return {
        ...s,
        course: previous === undefined ? rest : { ...rest, defaultVehicleId: previous },
      };
    },
  };
}

/**
 * 編集中のコースの背景（環境）を変える操作（Undo / Redo の単位）。
 * 組み込みの環境・部品の組み合わせ（`findEnvironment` で見つかるもの）だけを選べる。見つからない ID・変化なしは `undefined`。
 */
export function setEnvironmentCommand(
  state: EditorState,
  environmentId: string,
): Command<EditorState> | undefined {
  if (findEnvironment(environmentId, []) === undefined) return undefined;
  const previous = state.course.defaultEnvironmentId;
  if (previous === environmentId) return undefined;
  return {
    label: '背景を変更',
    apply: (s) => ({ ...s, course: { ...s.course, defaultEnvironmentId: environmentId } }),
    revert: (s) => {
      const { defaultEnvironmentId: _removed, ...rest } = s.course;
      return {
        ...s,
        course: previous === undefined ? rest : { ...rest, defaultEnvironmentId: previous },
      };
    },
  };
}
